import React, { useEffect, useState } from "react";
import {
  BellOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { usePermissions } from "../../PermissionsProvider";
import { CompanyService } from "../../services/CompanyService";
import { Company } from "../../model_gen/company";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { UserDropdown } from "../ui/dropdown";
import { ThemeToggle } from "../ui/theme-toggle";
import Search from "../Search/Search";
import useStore from "../../store/useStore";
import AlertTray from "../Notifications/AlertTray";
import { NotificationService } from "../../services/NotificationService";
import {
  Notification,
  NotificationStatus,
  NotificationType,
} from "../../model_gen/notification";
import { useWebSocket } from "../../utils/Websockets";
import { cn } from "../../lib/utils";

const HeaderComponent: React.FC = () => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const { dataReadyTutorial } = useStore();
  const [company, setCompany] = useState<Company | null>(null);
  const { user, logout } = useAuth0();
  const [alertTrayVisible, setAlertTrayVisible] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  useEffect(() => {
    if (apiKey) {
      CompanyService.getCompany(null, apiKey).subscribe(
        (result: Company) => {
          setCompany(result);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [apiKey]);

  useEffect(() => {
    if (apiKey) {
      // Fetch unread notifications
      fetchUnreadNotifications();
    }
  }, [apiKey, refreshTrigger]);

  const fetchUnreadNotifications = () => {
    // Only get CREATED status notifications (unread)
    const notificationStatuses: NotificationStatus[] = ["CREATED"];
    // Only include FINETUNING_COMPLETE notifications, exclude FAULT
    const excludeTypes: NotificationType[] = ["FAULT"];
    NotificationService.getNotifications(
      notificationStatuses,
      [],
      excludeTypes,
      null,
      apiKey
    ).subscribe(
      (result: Notification[]) => {
        setUnreadCount(result ? result.length : 0);
      },
      (error: any) => {
        console.error("Error fetching notification count:", error);
      }
    );
  };

  // Handle new notification from websocket
  const handleNewNotification = () => {
    // A new notification arrived via websocket, trigger a refresh
    setRefreshTrigger((prev) => prev + 1);
  };

  // Set up websocket listener for notifications
  useWebSocket({
    key: company?.id,
    eventType: "NOTIFICATION",
    onMessage: handleNewNotification,
  });

  const toggleAlertTray = () => {
    setAlertTrayVisible(!alertTrayVisible);
    if (alertTrayVisible) {
      fetchUnreadNotifications();
    }
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: `${window.location.origin}` } });
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <div className="flex items-center justify-between w-full h-16 px-6">
      {/* Left Section - Search */}
      <div className="flex items-center flex-1 max-w-2xl mr-6">
        <Search disabled={!dataReadyTutorial} />
      </div>

      {/* Right Section - Action buttons and user menu */}
      <div className="flex items-center gap-4 flex-shrink-0">
        {/* Documentation Button */}
        <Button
          variant="ghost"
          size="icon"
          asChild
          className="text-muted-foreground hover:text-foreground"
        >
          <a
            href="https://docs.trymaitai.ai"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Documentation"
          >
            <QuestionCircleOutlined className="w-5 h-5" />
          </a>
        </Button>

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Notifications Button */}
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleAlertTray}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Notifications"
          >
            <BellOutlined className="w-5 h-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* Settings Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/settings")}
          disabled={!dataReadyTutorial}
          className={cn(
            "text-muted-foreground hover:text-foreground",
            !dataReadyTutorial && "opacity-50 cursor-not-allowed"
          )}
          aria-label="Settings"
          title={
            !dataReadyTutorial
              ? "Make a request using the Maitai SDK to view this page"
              : "Settings"
          }
        >
          <SettingOutlined className="w-5 h-5" />
        </Button>

        {/* User Dropdown */}
        <UserDropdown
          user={user}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
      </div>

      {/* Notifications Drawer */}
      <AlertTray
        visible={alertTrayVisible}
        onClose={() => setAlertTrayVisible(false)}
        refreshTrigger={refreshTrigger}
        onNotificationsRead={fetchUnreadNotifications}
      />
    </div>
  );
};

export default HeaderComponent;
